/**
 * Tab Toggle Utility Functions
 * Provides accessible tab switching functionality with keyboard navigation
 * Compatible with existing DOM helpers and follows project standards
 */

import { addClass, removeClass, hasClass } from './dom-helpers.js';

/**
 * Default configuration for tab toggle functionality
 */
const DEFAULT_CONFIG = {
  tabSelector: '.tab',
  contentSelector: '.tab-content',
  activeClass: 'active',
  autoInit: true,
  keyboardNav: true,
  animation: true,
  dataAttribute: 'data-tab-target',
  containerAttribute: 'data-tab-container'
};

/**
 * Tab Toggle Manager Class
 * Handles tab switching, keyboard navigation, and accessibility
 */
class TabToggleManager {
  constructor(container, config = {}) {
    this.container = container;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.tabs = [];
    this.contents = [];
    this.activeTabIndex = 0;
    this.isInitialized = false;
    
    this.init();
  }

  /**
   * Initialize tab toggle functionality
   */
  init() {
    if (this.isInitialized) return;
    
    this.findElements();
    this.setupAccessibility();
    this.bindEvents();
    this.setInitialState();
    
    this.isInitialized = true;
    
    if (this.config.debug) {
      console.log('TabToggleManager initialized:', {
        container: this.container,
        tabs: this.tabs.length,
        contents: this.contents.length
      });
    }
  }

  /**
   * Find tab and content elements
   */
  findElements() {
    // Find tabs within container
    this.tabs = Array.from(
      this.container.querySelectorAll(this.config.tabSelector)
    );
    
    // Find content elements
    this.contents = Array.from(
      this.container.querySelectorAll(this.config.contentSelector)
    );
    
    // If no content found in container, try to find by data attributes
    if (this.contents.length === 0) {
      this.tabs.forEach(tab => {
        const targetId = tab.getAttribute(this.config.dataAttribute);
        if (targetId) {
          const content = document.querySelector(targetId);
          if (content) {
            this.contents.push(content);
          }
        }
      });
    }
  }

  /**
   * Setup accessibility attributes
   */
  setupAccessibility() {
    this.tabs.forEach((tab, index) => {
      // Set ARIA attributes
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
      
      // Set aria-controls if content exists
      if (this.contents[index]) {
        const contentId = this.contents[index].id || `tab-content-${index}`;
        if (!this.contents[index].id) {
          this.contents[index].id = contentId;
        }
        tab.setAttribute('aria-controls', contentId);
      }
    });

    this.contents.forEach((content, index) => {
      // Set ARIA attributes for content
      content.setAttribute('role', 'tabpanel');
      content.setAttribute('aria-labelledby', this.tabs[index]?.id || `tab-${index}`);
      content.setAttribute('aria-hidden', 'true');
    });
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Click events for tabs
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.setActiveTab(index);
      });
    });

    // Keyboard navigation
    if (this.config.keyboardNav) {
      this.container.addEventListener('keydown', (e) => {
        this.handleKeyboard(e);
      });
    }
  }

  /**
   * Set initial state
   */
  setInitialState() {
    // Find initially active tab
    const activeTab = this.tabs.find(tab => hasClass(tab, this.config.activeClass));
    const activeIndex = activeTab ? this.tabs.indexOf(activeTab) : 0;
    
    this.setActiveTab(activeIndex, false);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} e - Keyboard event
   */
  handleKeyboard(e) {
    const { key } = e;
    const currentIndex = this.activeTabIndex;
    
    switch (key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        this.setActiveTab((currentIndex + 1) % this.tabs.length);
        break;
        
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        this.setActiveTab((currentIndex - 1 + this.tabs.length) % this.tabs.length);
        break;
        
      case 'Home':
        e.preventDefault();
        this.setActiveTab(0);
        break;
        
      case 'End':
        e.preventDefault();
        this.setActiveTab(this.tabs.length - 1);
        break;
        
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.tabs[currentIndex].click();
        break;
    }
  }

  /**
   * Set active tab by index
   * @param {number} index - Tab index
   * @param {boolean} focus - Whether to focus the tab
   */
  setActiveTab(index, focus = true) {
    if (index < 0 || index >= this.tabs.length) {
      console.warn('TabToggleManager: Invalid tab index:', index);
      return;
    }

    // Remove active state from all tabs and contents
    this.tabs.forEach((tab, i) => {
      removeClass(tab, this.config.activeClass);
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
      
      if (this.contents[i]) {
        removeClass(this.contents[i], this.config.activeClass);
        this.contents[i].setAttribute('aria-hidden', 'true');
        this.contents[i].style.display = 'none';
      }
    });

    // Set active state for selected tab and content
    const activeTab = this.tabs[index];
    const activeContent = this.contents[index];
    
    addClass(activeTab, this.config.activeClass);
    activeTab.setAttribute('aria-selected', 'true');
    activeTab.setAttribute('tabindex', '0');
    
    if (activeContent) {
      addClass(activeContent, this.config.activeClass);
      activeContent.setAttribute('aria-hidden', 'false');
      activeContent.style.display = 'block';
    }

    this.activeTabIndex = index;

    // Focus the active tab
    if (focus) {
      activeTab.focus();
    }

    // Emit custom event
    this.emitTabChangeEvent(index, activeTab, activeContent);
  }

  /**
   * Toggle tab by index (if already active, do nothing)
   * @param {number} index - Tab index
   */
  toggleTab(index) {
    if (index === this.activeTabIndex) {
      return; // Already active, no toggle needed
    }
    this.setActiveTab(index);
  }

  /**
   * Get active tab index
   * @returns {number} Active tab index
   */
  getActiveTabIndex() {
    return this.activeTabIndex;
  }

  /**
   * Get active tab element
   * @returns {HTMLElement|null} Active tab element
   */
  getActiveTab() {
    return this.tabs[this.activeTabIndex] || null;
  }

  /**
   * Get active content element
   * @returns {HTMLElement|null} Active content element
   */
  getActiveContent() {
    return this.contents[this.activeTabIndex] || null;
  }

  /**
   * Emit custom tab change event
   * @param {number} index - Tab index
   * @param {HTMLElement} tab - Tab element
   * @param {HTMLElement} content - Content element
   */
  emitTabChangeEvent(index, tab, content) {
    const event = new CustomEvent('tabChange', {
      detail: {
        index,
        tab,
        content,
        manager: this
      },
      bubbles: true
    });
    
    this.container.dispatchEvent(event);
  }

  /**
   * Destroy tab toggle functionality
   */
  destroy() {
    // Remove event listeners
    this.tabs.forEach(tab => {
      tab.removeEventListener('click', this.setActiveTab);
    });
    
    if (this.config.keyboardNav) {
      this.container.removeEventListener('keydown', this.handleKeyboard);
    }
    
    this.isInitialized = false;
  }
}

/**
 * Initialize tab toggle for a container
 * @param {HTMLElement|string} container - Container element or selector
 * @param {Object} config - Configuration options
 * @returns {TabToggleManager} Tab toggle manager instance
 */
export function initTabToggle(container, config = {}) {
  const element = typeof container === 'string' 
    ? document.querySelector(container) 
    : container;
    
  if (!element) {
    console.warn('TabToggleManager: Container not found:', container);
    return null;
  }
  
  return new TabToggleManager(element, config);
}

/**
 * Toggle a specific tab by index
 * @param {number} index - Tab index
 * @param {HTMLElement|string} container - Container element or selector
 */
export function toggleTab(index, container) {
  const manager = getTabManager(container);
  if (manager) {
    manager.toggleTab(index);
  }
}

/**
 * Set active tab by index
 * @param {number} index - Tab index
 * @param {HTMLElement|string} container - Container element or selector
 */
export function setActiveTab(index, container) {
  const manager = getTabManager(container);
  if (manager) {
    manager.setActiveTab(index);
  }
}

/**
 * Get tab manager instance for container
 * @param {HTMLElement|string} container - Container element or selector
 * @returns {TabToggleManager|null} Tab manager instance
 */
function getTabManager(container) {
  const element = typeof container === 'string' 
    ? document.querySelector(container) 
    : container;
    
  if (!element) {
    console.warn('TabToggleManager: Container not found:', container);
    return null;
  }
  
  // Try to get existing manager from element
  return element._tabToggleManager || null;
}

/**
 * Auto-initialize all tab containers on page load
 * Looks for elements with data-tab-container attribute
 */
export function autoInitTabToggles() {
  const containers = document.querySelectorAll(`[${DEFAULT_CONFIG.containerAttribute}]`);
  
  containers.forEach(container => {
    const config = {
      tabSelector: container.dataset.tabSelector || DEFAULT_CONFIG.tabSelector,
      contentSelector: container.dataset.contentSelector || DEFAULT_CONFIG.contentSelector,
      activeClass: container.dataset.activeClass || DEFAULT_CONFIG.activeClass,
      keyboardNav: container.dataset.keyboardNav !== 'false',
      animation: container.dataset.animation !== 'false'
    };
    
    const manager = initTabToggle(container, config);
    if (manager) {
      // Store manager reference on element
      container._tabToggleManager = manager;
    }
  });
}

/**
 * Initialize tab toggles when DOM is ready
 */
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInitTabToggles);
  } else {
    autoInitTabToggles();
  }
}

// Export default for convenience
export default {
  initTabToggle,
  toggleTab,
  setActiveTab,
  autoInitTabToggles,
  TabToggleManager
};
